import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import jwtAuthAxios from "app/services/auth/jwtAuth";

/**
 * GP two-phase card payment test (pre-authorization) — CA → CI → CF / CU on a REAL terminal via DLA.
 * Internal test tool for operators at the terminal (SK labels). Shown only for Global Payments.
 *
 * Flow: CA (hold) returns previousRequestId + the terminal's hostTransId/tid/maskedPan/sequenceNumber;
 * those are kept and passed back on increment (CI) / completion (CF) / void (CU).
 *
 * Endpoints: POST /api/v1/test/device/preauth[/increment|/complete|/void].
 */
const PreauthTestPanel = ({ customerCode, deviceId }) => {
  const [caAmount, setCaAmount] = useState("1");
  const [ciAmount, setCiAmount] = useState("1");
  const [cfAmount, setCfAmount] = useState("1");
  const [busy, setBusy] = useState(""); // "CA" | "CI" | "CF" | "CU" | ""
  const [ctx, setCtx] = useState(null); // { previousRequestId, hostTransId, terminalId, cardNumber, sequenceNumber }
  const [lastOp, setLastOp] = useState("");
  const [response, setResponse] = useState(null);
  const [error, setError] = useState("");

  const ready = Boolean(customerCode && deviceId);
  const hasPreauth = Boolean(ctx && ctx.previousRequestId);

  const baseBody = () => ({
    customerCode: String(customerCode || "").trim(),
    deviceId: Number(deviceId),
  });

  const refBody = () =>
    ctx
      ? {
          previousRequestId: ctx.previousRequestId,
          hostTransId: ctx.hostTransId,
          terminalId: ctx.terminalId,
          cardNumber: ctx.cardNumber,
          sequenceNumber: ctx.sequenceNumber,
          transactionDate: ctx.transactionDate,
        }
      : {};

  const run = async (op, url, body, onOk) => {
    setBusy(op);
    setError("");
    setLastOp(op);
    setResponse(null);
    try {
      const { data } = await jwtAuthAxios.post(url, { ...baseBody(), ...body });
      setResponse(data);
      if (onOk) onOk(data);
    } catch (e) {
      const d = e?.response?.data;
      setResponse(d || null);
      setError(d?.message || e?.message || "Chyba požiadavky");
    } finally {
      setBusy("");
    }
  };

  // CA — captures the pre-auth reference (previousRequestId + host transaction identifiers).
  const doCA = () =>
    run("CA", "/api/v1/test/device/preauth", { amount: Number(caAmount) }, (data) => {
      const r = data?.result || {};
      const inner = r?.result || {};
      // The terminal's host transaction identifiers live under the normalized `cardData`
      // (mapGlobalPaymentsResult), not directly on `inner` — read them from there so CF/CI/CU
      // carry the CA reference and the terminal does not prompt for TID/sequence number manually.
      const cd = inner.cardData || {};
      const raw = inner.rawResponse || {};
      if (r.previousRequestId) {
        setCtx({
          previousRequestId: r.previousRequestId,
          hostTransId: cd.hostTransId,
          terminalId: cd.tid,
          cardNumber: cd.maskedPan,
          sequenceNumber: cd.sequenceNumber,
          // GP raw format yyyyMMddHHmmss (cardData.transactionDate is date-only) — the terminal needs
          // the original date on CF/CI/CU, otherwise it prompts for it (defaulting to today).
          transactionDate: raw.transactionDate,
        });
      }
    });

  const doCI = () =>
    run("CI", "/api/v1/test/device/preauth/increment", {
      amount: Number(ciAmount),
      ...refBody(),
    });

  const doCF = () =>
    run("CF", "/api/v1/test/device/preauth/complete", {
      amount: Number(cfAmount),
      ...refBody(),
    });

  const doCU = () =>
    run("CU", "/api/v1/test/device/preauth/void", { ...refBody() });

  const reset = () => {
    setCtx(null);
    setLastOp("");
    setResponse(null);
    setError("");
  };

  const fieldSx = { width: 140 };

  return (
    <Box
      sx={{
        mt: 1,
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Test predautorizácie (GP terminál)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Dvojfázová platba na reálnom termináli: CA (predautorizácia) → CI (navýšenie) → CF
        (dokončenie) alebo CU (zrušenie holdu). Najprv CA, potom ostatné operácie referencujú na ňu.
      </Typography>

      {!ready && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Chýba kód zákazníka alebo ID zariadenia — ulož zariadenie a skús znova.
        </Alert>
      )}

      {/* CA */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
        <TextField
          label="Suma CA (€)"
          size="small"
          type="number"
          value={caAmount}
          onChange={(e) => setCaAmount(e.target.value)}
          sx={fieldSx}
          inputProps={{ min: 0, step: "0.01" }}
        />
        <Button
          variant="contained"
          disabled={!ready || busy !== ""}
          onClick={doCA}
          startIcon={busy === "CA" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          CA – Predautorizácia
        </Button>
        {hasPreauth && (
          <Button variant="text" color="secondary" disabled={busy !== ""} onClick={reset}>
            Reset
          </Button>
        )}
      </Stack>

      {hasPreauth && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ display: "block" }}>
            <strong>previousRequestId:</strong> {ctx.previousRequestId}
          </Typography>
          <Typography variant="caption" sx={{ display: "block" }}>
            hostTransId: {ctx.hostTransId || "—"} · terminalId: {ctx.terminalId || "—"} · card:{" "}
            {ctx.cardNumber || "—"} · seq: {ctx.sequenceNumber || "—"}
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 1 }} />

      {/* CI / CF / CU — require a CA first */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
        <TextField
          label="Navýšenie (€)"
          size="small"
          type="number"
          value={ciAmount}
          onChange={(e) => setCiAmount(e.target.value)}
          sx={fieldSx}
          inputProps={{ min: 0, step: "0.01" }}
        />
        <Button
          variant="outlined"
          disabled={!hasPreauth || busy !== ""}
          onClick={doCI}
          startIcon={busy === "CI" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          CI – Navýšenie
        </Button>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
        <TextField
          label="Finálna suma (€)"
          size="small"
          type="number"
          value={cfAmount}
          onChange={(e) => setCfAmount(e.target.value)}
          sx={fieldSx}
          inputProps={{ min: 0, step: "0.01" }}
        />
        <Button
          variant="contained"
          color="success"
          disabled={!hasPreauth || busy !== ""}
          onClick={doCF}
          startIcon={busy === "CF" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          CF – Dokončenie
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={!hasPreauth || busy !== ""}
          onClick={doCU}
          startIcon={busy === "CU" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          CU – Zrušiť hold
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {response && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Odpoveď ({lastOp}):
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              p: 1,
              maxHeight: 240,
              overflow: "auto",
              fontSize: 11,
              lineHeight: 1.4,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(response, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PreauthTestPanel;
